import { Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';

export interface LoginUiConfig {
  brandName: string;
  tabTitle?: string;
  welcomeTitle: string;
  logoPath: string;
  pageClass: string;
  gradientStart: string;
  gradientEnd: string;
  logoBgStart: string;
  logoBgMid: string;
  logoBgEnd: string;
  titleColor: string;
  subtitleColor: string;
  btnBgColor?: string;
  btnHoverBgColor?: string;
}

export const DEFAULT_LOGIN_UI: LoginUiConfig = {
  brandName: 'SocietyXpert',
  tabTitle: 'SocietyXpert - Simplified Society Management App',
  welcomeTitle: 'Welcome back',
  logoPath: 'assets/Images/societyxpert_logo.png',
  pageClass: 'login-ui-default',
  gradientStart: '#e4e5e6',
  gradientEnd: '#00416a',
  logoBgStart: '#ffffff',
  logoBgMid: '#e9ecef',
  logoBgEnd: '#dde2e6',
  titleColor: '#212529',
  subtitleColor: '#6c757d',
  btnBgColor: '#186391',
  btnHoverBgColor: '#124a6b',
};

/** UI config for the Legacy Care theme (legacyconnect.co.in) */
export const LEGACY_LOGIN_UI: LoginUiConfig = {
  brandName: 'Legacy Care',
  tabTitle: 'Legacy Care - Simplified Society Management App',
  welcomeTitle: 'Welcome back',
  logoPath: 'assets/Images/page1.png',
  pageClass: 'login-ui-legacyconnect',
  gradientStart: '#3a3429',
  gradientEnd: '#1e1f24',
  logoBgStart: '#3f3a30',
  logoBgMid: '#2d2e35',
  logoBgEnd: '#24252a',
  titleColor: '#1e1f24',
  subtitleColor: '#6b7280',
  btnBgColor: '#C8A35A',
  btnHoverBgColor: '#A8843F',
};

/** UI config for the SocietyXpert theme (societyxpert.com / portalwiz.in) */
export const SOCIETY_LOGIN_UI: LoginUiConfig = {
  brandName: 'SocietyXpert',
  tabTitle: 'SocietyXpert - Simplified Society Management App',
  welcomeTitle: 'Welcome back',
  logoPath: 'assets/Images/societyxpert_logo.png',
  pageClass: 'login-ui-societyxpert',
  gradientStart: '#e4e5e6',
  gradientEnd: '#00416a',
  logoBgStart: '#ffffff',
  logoBgMid: '#e9ecef',
  logoBgEnd: '#dde2e6',
  titleColor: '#212529',
  subtitleColor: '#6c757d',
  btnBgColor: '#186391',
  btnHoverBgColor: '#124a6b',
};

/** UI config for the My Austin theme — orange (#F07B20) + charcoal (#4A4A4A) from the logo */
export const AUSTIN_LOGIN_UI: LoginUiConfig = {
  brandName: 'My Austin',
  tabTitle: 'My Austin - Simplified Society Management App',
  welcomeTitle: 'Welcome back',
  logoPath: 'assets/Images/austin.jpeg',
  pageClass: 'login-ui-austin',
  gradientStart: '#fff8f2',
  gradientEnd: '#F07B20',
  logoBgStart: '#ffffff',
  logoBgMid: '#fff3e8',
  logoBgEnd: '#ffe8cc',
  titleColor: '#000000',
  subtitleColor: '#6b7280',
  btnBgColor: '#000000',
  btnHoverBgColor: '#1a1a1a',
};

/** UI config for Mangalam theme (mangalam.societyxpert.com) */
export const MANGALAM_LOGIN_UI: LoginUiConfig = {
  brandName: 'Mangalam SocietyXpert',
  tabTitle: 'Mangalam - SocietyXpert Management App',
  welcomeTitle: 'Welcome back',
  logoPath: 'assets/Images/societyxpert_logo.png',
  pageClass: 'login-ui-mangalam',
  gradientStart: '#e4e5e6',
  gradientEnd: '#00416a',
  logoBgStart: '#ffffff',
  logoBgMid: '#e9ecef',
  logoBgEnd: '#dde2e6',
  titleColor: '#212529',
  subtitleColor: '#6c757d',
  btnBgColor: '#186391',
  btnHoverBgColor: '#124a6b',
};

/** UI config for Relation Nest theme (relationnest.com) */
export const RELATIONNEST_LOGIN_UI: LoginUiConfig = {
  brandName: 'Relation Nest',
  tabTitle: 'Relation Nest - Simplified Society Management App',
  welcomeTitle: 'Welcome back',
  logoPath: 'assets/Images/Relation_nest.jpeg',
  pageClass: 'login-ui-relationnest',
  gradientStart: '#e4e5e6',
  gradientEnd: '#00416a',
  logoBgStart: '#ffffff',
  logoBgMid: '#e9ecef',
  logoBgEnd: '#dde2e6',
  titleColor: '#212529',
  subtitleColor: '#6c757d',
  btnBgColor: '#186391',
  btnHoverBgColor: '#124a6b',
};

export const LOGIN_UI_BY_HOST_RULES: Array<{ matcher: (host: string) => boolean; ui: LoginUiConfig }> = [
  {
    matcher: (host) => host === 'legacyconnect.co.in' || host === 'www.legacyconnect.co.in',
    ui: LEGACY_LOGIN_UI,
  },
  {
    matcher: (host) => host.includes('austin'),
    ui: AUSTIN_LOGIN_UI,
  },
  {
    matcher: (host) => host.includes('mangalam'),
    ui: MANGALAM_LOGIN_UI,
  },
  {
    matcher: (host) => host.includes('relationnest'),
    ui: RELATIONNEST_LOGIN_UI,
  },
  {
    matcher: (host) => host.includes('societyxpert.com'),
    ui: SOCIETY_LOGIN_UI,
  },
  {
    matcher: (host) => host.includes('portalwiz.in'),
    ui: {
      brandName: 'PortalWiz',
      tabTitle: 'PortalWiz - Society Management App',
      welcomeTitle: 'Sign in to continue',
      logoPath: 'assets/Images/societyxpert_logo.png',
      pageClass: 'login-ui-portalwiz',
      gradientStart: '#eaf4ff',
      gradientEnd: '#2b5aa6',
      logoBgStart: '#ffffff',
      logoBgMid: '#e8f0ff',
      logoBgEnd: '#d8e7ff',
      titleColor: '#1f2937',
      subtitleColor: '#6b7280',
    },
  },
];

export function getHostName(): string {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return '';
  }
  return window.location.hostname.toLowerCase().trim();
}

/**
 * Resolves which login UI config to use:
 * 1. Matches by hostname (production / staging domains).
 * 2. On localhost — uses the `environment.theme` injected at build-time
 *    so `npm run start:legacy` → Legacy Care UI, `npm run start:society` → SocietyXpert UI.
 */
export function resolveLoginUiFromHost(): LoginUiConfig {
  const host = getHostName();

  // On a known domain, always use hostname matching
  const matchedRule = LOGIN_UI_BY_HOST_RULES.find((rule) => rule.matcher(host));
  if (matchedRule) {
    return matchedRule.ui;
  }

  // On localhost (or unknown host) — fall back to build-time environment theme
  const theme = (environment as any).theme as 'legacy' | 'society' | 'austin' | 'mangalam' | 'relationnest' | undefined;
  if (theme === 'legacy') {
    return LEGACY_LOGIN_UI;
  }
  if (theme === 'society') {
    return SOCIETY_LOGIN_UI;
  }
  if (theme === 'austin') {
    return AUSTIN_LOGIN_UI;
  }
  if (theme === 'mangalam') {
    return MANGALAM_LOGIN_UI;
  }
  if (theme === 'relationnest') {
    return RELATIONNEST_LOGIN_UI;
  }

  return DEFAULT_LOGIN_UI;
}

/**
 * Dynamically updates the browser tab name (document title) and tab favicon
 * based on the resolved environment / host branding.
 */
export function applyEnvironmentBranding(titleService?: Title, uiConfig?: LoginUiConfig): void {
  const config = uiConfig ?? resolveLoginUiFromHost();
  const envTabTitle = (environment as any).tabTitle as string | undefined;
  const envLogoPath = (environment as any).logoPath as string | undefined;

  const targetTitle =
    config.tabTitle ||
    envTabTitle ||
    `${config.brandName || (environment as any).appName || 'SocietyXpert'} - Simplified Society Management App`;
  const targetLogo = config.logoPath || envLogoPath || 'assets/Images/societyxpert_logo.png';

  if (titleService) {
    titleService.setTitle(targetTitle);
  } else if (typeof document !== 'undefined') {
    document.title = targetTitle;
  }

  if (typeof document !== 'undefined') {
    const favicon = document.getElementById('app-favicon') as HTMLLinkElement | null;
    if (favicon) {
      favicon.href = targetLogo;
    }
  }
}


